import React, { useState } from "react";
import { Copy, Sparkle, Hash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ModelSelector } from "../components/ModelSelector";

export default function CaptionStudio() {
  const { setCredits } = useAuth();
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("tiktok");
  const [style, setStyle] = useState("viral");
  const [model, setModel] = useState(null);
  const [compare, setCompare] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!topic.trim()) { toast.error("Add a topic"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/ai/caption", { topic, platform, style, model, compare });
      if (typeof data?._credits_remaining === "number") setCredits(data._credits_remaining);
      setResult(data);
      toast.success(data.compare ? "Compared across models" : "Caption generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const copy = (t, l) => { navigator.clipboard.writeText(t); toast.success(`${l} copied`); };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="caption-studio">
      <p className="text-[#FF3B6B] text-sm font-mono">// CAPTION ENGINE</p>
      <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Engineer the perfect caption</h1>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 glass rounded-xl p-6 space-y-5">
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Topic</label>
            <textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={3} placeholder="What's the video about?" className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm focus:border-[#FF3B6B] focus:outline-none resize-none" data-testid="caption-topic-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/60 uppercase tracking-wider">Platform</label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger className="mt-2 bg-white/5 border-white/10 capitalize" data-testid="caption-platform-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["tiktok", "youtube", "instagram"].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-white/60 uppercase tracking-wider">Style</label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger className="mt-2 bg-white/5 border-white/10 capitalize" data-testid="caption-style-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["viral", "professional", "casual", "humorous"].map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <ModelSelector type="text" action="caption" model={model} compare={compare} onChange={({ model: m, compare: c }) => { setModel(m); setCompare(c); }} />
          <button onClick={submit} disabled={loading} className="w-full btn-primary py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="generate-caption-btn">
            {loading ? (compare ? "Comparing 3 models..." : "Generating...") : <><Sparkle weight="fill" size={16} /> {compare ? "Compare across models" : "Generate caption"}</>}
          </button>
        </div>

        <div className="lg:col-span-7 space-y-4">
          {!result && !loading && (
            <div className="glass rounded-xl p-12 text-center text-white/40 border-dashed">
              <Hash size={40} className="mx-auto mb-3 text-white/20" />
              <p>Your caption will appear here</p>
            </div>
          )}
          {result && !result.compare && <CaptionCards data={result} copy={copy} />}
          {result?.compare && (
            <div className="space-y-6">
              {result.variants.map((v) => (
                <div key={v.model} className="space-y-3" data-testid={`variant-${v.model}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono px-2 py-1 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30">{v.label}</span>
                    {v.error && <span className="text-xs text-red-400">failed: {v.error}</span>}
                  </div>
                  {v.data && <CaptionCards data={v.data} copy={copy} />}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const CaptionCards = ({ data, copy }) => (
  <>
    <div className="glass rounded-xl p-6" data-testid="caption-result">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-[#FF3B6B] font-mono uppercase tracking-wider">Caption</p>
        <button onClick={() => copy(data.caption, "Caption")} className="text-white/40 hover:text-white"><Copy size={14} /></button>
      </div>
      <p className="text-lg">{data.caption}</p>
      <p className="mt-3 text-2xl">{data.emojis}</p>
    </div>
    <div className="glass rounded-xl p-6" data-testid="caption-hashtags">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-[#00E5FF] font-mono uppercase tracking-wider">Hashtags</p>
        <button onClick={() => copy(data.hashtags?.map(h => "#" + h).join(" "), "Hashtags")} className="text-white/40 hover:text-white"><Copy size={14} /></button>
      </div>
      <div className="flex flex-wrap gap-2">
        {data.hashtags?.map((h, i) => <span key={i} className="text-sm font-mono text-[#00E5FF]">#{h.replace(/^#/, "")}</span>)}
      </div>
    </div>
    <div className="glass rounded-xl p-6" data-testid="caption-seo">
      <p className="text-xs text-[#EAB308] font-mono uppercase tracking-wider mb-2">SEO Keywords</p>
      <div className="flex flex-wrap gap-2">
        {data.seo_keywords?.map((k, i) => <span key={i} className="text-sm px-3 py-1 rounded-full bg-white/5 border border-white/10">{k}</span>)}
      </div>
    </div>
  </>
);
